import { useEffect, useRef } from 'react';

// Nasłuch klawiaturowy czytnika HID (Magellan) na stanowisku rejestracyjnym.
//
// Polecenie właściciela (dyktowanie, 2026-09-17): "mamy tutaj mieć skanowanie, ale za pomocą
// klawiatury (...) ten skaner jest podpięty cały czas. Jak ktoś go zeskanuje, to system od razu
// go wychwyci (...) bez opcji włączania kamery". Zero przycisku, zero kamery — czytnik "wpisuje"
// znaki kodu bardzo szybko i kończy Enterem, tak samo jak na bramie ochrony.
//
// Mechanizm jest CELOWO 1:1 odwzorowany z bojowo sprawdzonego nasłuchu bramy ochrony w
// innerwebbackend (spa/src/composables/useScannerListener.ts) — próg odstępu między znakami,
// zakończenie Enterem, odrzucanie dubletów tego samego odczytu w krótkim oknie, ignorowanie
// zdarzeń gdy fokus jest w polu tekstowym. JEDYNA świadoma różnica: tu NIE parsujemy odczytu
// (kind/value wg GateScanCodeParser) — właściciel chce surowy string na `/skan`, tak jak dziś
// robi to `handleScan` w RegistrationStation.js; parsuje go backend.

/** Odstęp > tyle ms między kolejnymi znakami = człowiek pisze na klawiaturze, nie skaner —
 * resetujemy bufor (skaner HID "wpisuje" znaki dużo szybciej, Fibonacci: CLAUDE.md). */
export const CHAR_GAP_MS = 34;

/** Bufor uznajemy za martwy (i czyścimy) tyle ms po ostatnim znaku, gdy nigdy nie przyszedł
 * Enter — zapobiega wiecznemu trzymaniu resztek nieukończonego skanu w pamięci (Fibonacci). */
export const BUFFER_IDLE_MS = 233;

/** Ignorujemy IDENTYCZNY surowy odczyt, jeśli poprzedni skan (dowolny) wyemitowaliśmy krócej
 * niż tyle ms temu — obrona w głąb, czysto UX (mniej zbędnych requestów), backend i tak ma
 * własny autorytatywny debounce. Fibonacci: 2584 ms. */
export const DUPLICATE_WINDOW_MS = 2584;

/** Krócej niż tyle znaków po Enterze → to nie kod skanera (literówka/przypadkowy Enter). */
export const MIN_RAW_LENGTH = 8;

/** `true`, gdy aktywny element to pole tekstowe świadomie oznaczone jako ignorujące globalny
 * skaner (`data-scan-ignore`) — np. awaryjne pole ręcznego wpisania kodu, gdzie użytkownik sam
 * pisze na fizycznej klawiaturze i Enter ma trafić TYLKO do obsługi tego pola, nie do listenera. */
function isScanIgnoredField() {
  const el = document.activeElement;
  if (!el || !(el instanceof HTMLElement)) return false;

  const isTextField = el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable;
  if (!isTextField) return false;

  return el.hasAttribute('data-scan-ignore');
}

/**
 * Fabryka nasłuchu — czysta funkcja (bez Reacta), żeby dało się ją przetestować niezależnie od
 * komponentu i żeby `useStationScannerListener` poniżej był cienkim wrapperem cyklu życia.
 */
export function createStationScannerListener(onScan, opts = {}) {
  const minLength = opts.minLength ?? MIN_RAW_LENGTH;

  let buffer = '';
  let lastCharAt = 0;
  let idleTimer = null;
  let listening = false;
  let lastEmittedRaw = null;
  let lastEmittedAt = 0;

  function clearIdleTimer() {
    if (idleTimer !== null) {
      clearTimeout(idleTimer);
      idleTimer = null;
    }
  }

  function resetBuffer() {
    buffer = '';
    clearIdleTimer();
  }

  function scheduleIdleClear() {
    clearIdleTimer();
    idleTimer = setTimeout(resetBuffer, BUFFER_IDLE_MS);
  }

  function handleKeydown(e) {
    if (isScanIgnoredField()) return;

    const now = performance.now();
    // Odstęp od poprzedniego znaku za duży na skaner — to, co jest w buforze, nie jest skanem.
    if (buffer.length > 0 && now - lastCharAt > CHAR_GAP_MS) {
      resetBuffer();
    }

    if (e.key === 'Enter') {
      if (buffer.length >= minLength) {
        const raw = buffer;
        const isDuplicate = raw === lastEmittedRaw && now - lastEmittedAt < DUPLICATE_WINDOW_MS;
        if (!isDuplicate) {
          lastEmittedRaw = raw;
          lastEmittedAt = now;
          onScan(raw);
        }
      }
      resetBuffer();
      return;
    }

    if (e.key.length === 1) {
      buffer += e.key;
      lastCharAt = now;
      scheduleIdleClear();
    }
  }

  function start() {
    if (listening) return; // idempotentny — drugi start() nie dubluje nasłuchu
    listening = true;
    window.addEventListener('keydown', handleKeydown, true);
  }

  function stop() {
    if (!listening) return;
    listening = false;
    window.removeEventListener('keydown', handleKeydown, true);
    resetBuffer();
    lastEmittedRaw = null;
    lastEmittedAt = 0;
  }

  return { start, stop };
}

/**
 * Hook — nasłuch działa NIEZALEŻNIE od tego, jaki ekran stanowiska jest akurat wyrenderowany
 * (idle/powitanie/grupa/szkolenie/zaliczone). Montowany raz na życie komponentu (deps: []),
 * `onScan` czytany z refa, żeby świeża domknięta funkcja z każdego renderu rodzica nie
 * restartowała nasłuchu.
 */
export function useStationScannerListener(onScan) {
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  useEffect(() => {
    const listener = createStationScannerListener((raw) => onScanRef.current(raw));
    listener.start();
    return () => listener.stop();
  }, []);
}
